import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Database, 
  FileSpreadsheet, 
  HardDrive, 
  Users, 
  TrendingUp,
  Activity,
  CheckCircle,
  AlertTriangle
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface SystemStats {
  products: number;
  questions: number;
  leads: number;
  submissions: number;
  uploads: number;
  totalFileSize: number;
}

export default function Dashboard() {
  const [stats, setStats] = useState<SystemStats>({
    products: 0,
    questions: 0,
    leads: 0,
    submissions: 0,
    uploads: 0,
    totalFileSize: 0
  });
  const [loading, setLoading] = useState(true);
  const [connectionStatus, setConnectionStatus] = useState<'checking' | 'connected' | 'error'>('checking');

  useEffect(() => {
    loadSystemStats();
    checkConnection();
  }, []);

  const checkConnection = async () => {
    try {
      const { error } = await supabase.auth.getSession();
      setConnectionStatus(error ? 'error' : 'connected');
    } catch {
      setConnectionStatus('error');
    }
  };

  const loadSystemStats = async () => {
    try {
      const [
        productsResult,
        questionsResult,
        leadsResult,
        submissionsResult,
        uploadsResult
      ] = await Promise.all([
        supabase.from('products').select('id', { count: 'exact', head: true }),
        supabase.from('questions').select('id', { count: 'exact', head: true }),
        supabase.from('leads').select('id', { count: 'exact', head: true }),
        supabase.from('submissions').select('id', { count: 'exact', head: true }),
        supabase.from('uploads').select('size_bytes')
      ]);

      const totalFileSize = uploadsResult.data?.reduce((sum, upload) => 
        sum + (upload.size_bytes || 0), 0) || 0;

      setStats({
        products: productsResult.count || 0,
        questions: questionsResult.count || 0,
        leads: leadsResult.count || 0,
        submissions: submissionsResult.count || 0,
        uploads: uploadsResult.data?.length || 0,
        totalFileSize
      });
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const statCards = [
    {
      title: 'Insurance Products',
      value: stats.products,
      icon: Database,
      description: 'Configured insurance products',
      color: 'text-blue-600',
      bgColor: 'bg-blue-100'
    },
    {
      title: 'Form Questions',
      value: stats.questions,
      icon: FileSpreadsheet,
      description: 'Dynamic form questions',
      color: 'text-green-600',
      bgColor: 'bg-green-100'
    },
    {
      title: 'Customer Leads',
      value: stats.leads,
      icon: Users,
      description: 'Generated leads',
      color: 'text-purple-600',
      bgColor: 'bg-purple-100'
    },
    {
      title: 'Form Submissions',
      value: stats.submissions,
      icon: TrendingUp,
      description: 'Completed submissions',
      color: 'text-orange-600',
      bgColor: 'bg-orange-100'
    },
    {
      title: 'Uploaded Files',
      value: stats.uploads,
      icon: HardDrive,
      description: 'Total file uploads',
      color: 'text-red-600',
      bgColor: 'bg-red-100'
    },
    {
      title: 'Storage Used',
      value: formatFileSize(stats.totalFileSize),
      icon: Activity,
      description: 'Total file storage',
      color: 'text-indigo-600',
      bgColor: 'bg-indigo-100',
      isString: true
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
          <p className="text-gray-600">Business Insurance System Overview</p>
        </div>
        <div className="flex items-center gap-2">
          {connectionStatus === 'connected' ? (
            <Badge variant="default" className="bg-green-500">
              <CheckCircle className="h-3 w-3 mr-1" />
              Connected
            </Badge>
          ) : connectionStatus === 'error' ? (
            <Badge variant="destructive">
              <AlertTriangle className="h-3 w-3 mr-1" />
              Connection Error
            </Badge>
          ) : (
            <Badge variant="secondary">
              <Activity className="h-3 w-3 mr-1 animate-pulse" />
              Checking...
            </Badge>
          )}
        </div>
      </div>

      {/* System Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {statCards.map((stat, index) => (
          <Card key={index}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {loading ? '...' : stat.isString ? stat.value : stat.value.toLocaleString()}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">{stat.description}</p>
                </div>
                <div className={`p-3 rounded-full ${stat.bgColor}`}>
                  <stat.icon className={`h-6 w-6 ${stat.color}`} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>System Health</CardTitle>
            <CardDescription>Current system status and health metrics</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Database Connection</span>
              <Badge variant={connectionStatus === 'connected' ? 'default' : 'destructive'}>
                {connectionStatus === 'connected' ? 'Healthy' : 'Error'}
              </Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Storage Bucket</span>
              <Badge variant="default">Active</Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">File Uploads</span>
              <Badge variant={stats.uploads > 0 ? 'default' : 'secondary'}>
                {stats.uploads > 0 ? 'Working' : 'No Data'}
              </Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Form Submissions</span>
              <Badge variant={stats.submissions > 0 ? 'default' : 'secondary'}>
                {stats.submissions > 0 ? 'Active' : 'No Data'}
              </Badge>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
            <CardDescription>Common administrative tasks</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="text-sm space-y-2">
              <p><strong>Database Health:</strong> Test connections and view metrics</p>
              <p><strong>Excel Exports:</strong> Download data for analysis</p>
              <p><strong>File Management:</strong> View and download uploaded files</p>
              <p><strong>Submission Tracker:</strong> Monitor customer submissions</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity */}
      <Card>
        <CardHeader>
          <CardTitle>System Information</CardTitle>
          <CardDescription>Current configuration and setup details</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-medium mb-3">Database Schema</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Products Table:</span>
                  <span className="font-mono">{stats.products} records</span>
                </div>
                <div className="flex justify-between">
                  <span>Questions Table:</span>
                  <span className="font-mono">{stats.questions} records</span>
                </div>
                <div className="flex justify-between">
                  <span>Leads Table:</span>
                  <span className="font-mono">{stats.leads} records</span>
                </div>
                <div className="flex justify-between">
                  <span>Submissions Table:</span>
                  <span className="font-mono">{stats.submissions} records</span>
                </div>
                <div className="flex justify-between">
                  <span>Uploads Table:</span>
                  <span className="font-mono">{stats.uploads} records</span>
                </div>
              </div>
            </div>
            <div>
              <h4 className="font-medium mb-3">Storage Information</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Storage Bucket:</span>
                  <span className="font-mono">uploads (private)</span>
                </div>
                <div className="flex justify-between">
                  <span>Total Files:</span>
                  <span className="font-mono">{stats.uploads}</span>
                </div>
                <div className="flex justify-between">
                  <span>Total Size:</span>
                  <span className="font-mono">{formatFileSize(stats.totalFileSize)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Average File Size:</span>
                  <span className="font-mono">
                    {stats.uploads > 0 ? formatFileSize(stats.totalFileSize / stats.uploads) : '0 Bytes'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
